import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	AlertTriangle,
	Check,
	ChevronLeft,
	Eye,
	Loader2,
	Pencil,
	// Plus,
	Sparkles,
	X,
} from "lucide-react";
import {
	getMonitoringWritingAssistantRun,
	listMonitoringWritingAssistantPersonas,
	listMonitoringWritingAssistantRuns,
	saveMonitoringWritingAssistantContent,
	WRITING_ASSISTANT_BUSY_STATUSES,
} from "../../../services/monitoringApi";
import WritingPreviewFixedPopover from "../WritingAssistance/Preview/WritingPreviewFixedPopover";
import WritingPreviewMarkdownWysiwyg from "../WritingAssistance/Preview/WritingPreviewMarkdownWysiwyg";
import { writingPreviewMarkdownComponents } from "../WritingAssistance/Preview/writingPreviewMarkdownComponents";
import FactsToVerifyList from "./analysisStrategy/FactsToVerifyList";
import { monitoringAnalysisStrategyReturnPath, safeReturnTo } from "../auditUtils";
// import WritingAssistantStartRunModal from "./WritingAssistantStartRunModal";
import { toast } from "react-toastify";
import {
	WritingAssistantContextPanel,
	WritingAssistantContextToggle,
	WritingAssistantCopyToggle,
	WritingAssistantDownloadToggle,
	WritingAssistantHistoryToggle,
} from "./WritingAssistantContextDialog";
import {
	hasWritingRunPersona,
	writingRunPersonaHistoryHints,
	writingRunPersonaHistoryLabel,
	writingRunPersonaId,
} from "./writingPersonaDisplay";
import {
	copyWritingAssistantDocument,
	downloadWritingAssistantDocx,
	hasExportableWritingContent,
} from "./writingAssistantDocxExport";

const HISTORY_REFRESH_MS = 15000;
const RUN_POLL_MS = 3000;

const STAGE_LABELS = {
	pending: "Starting…",
	fetching_competitors: "Fetching competitor pages…",
	competitors_ready: "Competitor pages fetched…",
	fetching_own_page: "Fetching your page…",
	own_page_ready: "Your page fetched…",
	analyzing_gaps: "Analyzing content gaps…",
	gaps_ready: "Drafting content…",
	drafting: "Drafting content…",
};

const WRITING_RUN_PIPELINE = [
	{
		key: "start",
		label: "Starting run",
		description: "Preparing your writing assistant job.",
		statuses: ["pending"],
	},
	{
		key: "competitors",
		label: "Fetching competitor pages",
		description: "Reviewing how competitors answer this prompt.",
		statuses: ["fetching_competitors", "competitors_ready"],
	},
	{
		key: "own_page",
		label: "Fetching your page",
		description: "Loading your current page content for comparison.",
		statuses: ["fetching_own_page", "own_page_ready"],
	},
	{
		key: "gaps",
		label: "Analyzing content gaps",
		description: "Identifying what to add or improve for AI visibility.",
		statuses: ["analyzing_gaps"],
	},
	{
		key: "draft",
		label: "Drafting content",
		description: "Generating your optimized page draft.",
		statuses: ["gaps_ready", "drafting"],
	},
];

const STAGE_DESCRIPTIONS = {
	pending: "Preparing your writing assistant job.",
	fetching_competitors: "Reviewing how competitors answer this prompt.",
	competitors_ready: "Competitor pages are ready — continuing setup.",
	fetching_own_page: "Loading your current page content for comparison.",
	own_page_ready: "Your page is ready — analyzing differences next.",
	analyzing_gaps: "Identifying what to add or improve for AI visibility.",
	gaps_ready: "Generating your optimized page draft.",
	drafting: "Generating your optimized page draft.",
};

const EMPTY_CONTENT = {
	meta_title: "",
	h1: "",
	meta_description: "",
	meta_keywords: [],
	url_slug: "",
	body_markdown: "",
};

const CARD_STYLE = {
	border: "1px solid var(--monitoring-border, #e5e7eb)",
	borderRadius: "0.75rem",
	background: "var(--monitoring-surface, #fff)",
};

const MARKDOWN_COMPONENTS = {
	h1: ({ children }) => <h3 className="h5 mt-4 mb-2">{children}</h3>,
	h2: ({ children }) => <h4 className="h6 mt-4 mb-2">{children}</h4>,
	h3: ({ children }) => <h5 className="h6 mt-3 mb-2">{children}</h5>,
	h4: ({ children }) => <h6 className="mt-3 mb-2">{children}</h6>,
	p: ({ children }) => <p className="mb-3 lh-base">{children}</p>,
	a: ({ href, children }) => (
		<a href={href} target="_blank" rel="noopener noreferrer">
			{children}
		</a>
	),
	ul: ({ children }) => <ul className="mb-3 ps-4">{children}</ul>,
	ol: ({ children }) => <ol className="mb-3 ps-4">{children}</ol>,
	blockquote: ({ children }) => (
		<blockquote className="border-start border-3 ps-3 text-muted">
			{children}
		</blockquote>
	),
	code: ({ children }) => <code className="bg-light px-1 rounded">{children}</code>,
	table: ({ children }) => (
		<div className="table-responsive mb-3">
			<table className="table table-sm table-bordered align-middle mb-0">
				{children}
			</table>
		</div>
	),
	thead: ({ children }) => <thead className="table-light">{children}</thead>,
	tbody: ({ children }) => <tbody>{children}</tbody>,
	tr: ({ children }) => <tr>{children}</tr>,
	th: ({ children }) => <th>{children}</th>,
	td: ({ children }) => <td>{children}</td>,
};

const CREATE_PREVIEW_MARKDOWN_COMPONENTS = {
	...writingPreviewMarkdownComponents,
	a: MARKDOWN_COMPONENTS.a,
	ol: ({ children }) => <ol className="writing-preview__md-ul mb-3">{children}</ol>,
	blockquote: MARKDOWN_COMPONENTS.blockquote,
	code: MARKDOWN_COMPONENTS.code,
	table: MARKDOWN_COMPONENTS.table,
	thead: MARKDOWN_COMPONENTS.thead,
	tbody: MARKDOWN_COMPONENTS.tbody,
	tr: MARKDOWN_COMPONENTS.tr,
	th: MARKDOWN_COMPONENTS.th,
	td: MARKDOWN_COMPONENTS.td,
};

function escapeRegExp(value) {
	return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripDuplicateLeadingH1(markdown, h1) {
	const trimmedH1 = String(h1 || "").trim();
	if (!trimmedH1 || !markdown) return String(markdown || "");

	const lines = String(markdown).replace(/\r\n/g, "\n").split("\n");
	let index = 0;
	while (index < lines.length && !lines[index].trim()) index += 1;
	if (index >= lines.length) return String(markdown);

	const line = lines[index].trim();
	const pattern = new RegExp(`^#\\s+${escapeRegExp(trimmedH1)}\\s*$`, "i");
	if (!pattern.test(line)) return String(markdown);

	lines.splice(index, 1);
	while (index < lines.length && !lines[index].trim()) lines.splice(index, 1);
	return lines.join("\n");
}

function resultError(result, fallback) {
	return result?.message || result?.error || result?.detail || fallback;
}

function unwrapRuns(result) {
	if (result?.ok === false) throw new Error(resultError(result, "Could not load writing history."));
	const value = result?.runs ?? result?.data?.runs ?? result?.data ?? result;
	return Array.isArray(value) ? value : [];
}

function unwrapRun(result, fallback) {
	if (result?.ok === false) throw new Error(resultError(result, fallback));
	const value = result?.run ?? result?.data?.run ?? result?.data ?? result;
	if (!value || typeof value !== "object") throw new Error(fallback);
	return value;
}

function writingRunAnalysisPath(run) {
	return monitoringAnalysisStrategyReturnPath(
		run?.analysis_id ?? run?.analysisId
	);
}

function isBusyStatus(status) {
	if (!status) return false;
	if (typeof WRITING_ASSISTANT_BUSY_STATUSES?.has === "function") {
		return WRITING_ASSISTANT_BUSY_STATUSES.has(status);
	}
	return Array.isArray(WRITING_ASSISTANT_BUSY_STATUSES)
		? WRITING_ASSISTANT_BUSY_STATUSES.includes(status)
		: !["complete", "failed"].includes(status);
}

function runHasDraftContent(run) {
	if (!run) return false;
	if (run.status === "complete") return true;
	const body = run.content?.body_markdown;
	if (typeof body === "string" && body.trim()) return true;
	const markdown = run.result?.recommendation?.markdown_recommendation;
	return typeof markdown === "string" && markdown.trim().length > 0;
}

function isRunWorkbenchReady(run) {
	if (!run || run.status === "failed") return false;
	return runHasDraftContent(run);
}

function shouldPollWritingRun(run) {
	if (!run) return false;
	if (isRunWorkbenchReady(run)) return false;
	return isBusyStatus(run.status);
}

function getWritingRunPipelineSteps(status) {
	const activeIndex = WRITING_RUN_PIPELINE.findIndex((step) =>
		step.statuses.includes(status)
	);
	const resolvedIndex = activeIndex >= 0 ? activeIndex : 0;
	return WRITING_RUN_PIPELINE.map((step, index) => ({
		label: step.label,
		active: index === resolvedIndex,
		done: index < resolvedIndex,
	}));
}

function WritingRunLoadingView({
	phase = "fetch",
	status,
	run,
	title,
	description,
}) {
	const steps =
		phase === "fetch"
			? [
					{ label: "Loading run details", active: true, done: false },
					{ label: "Preparing workbench", active: false, done: false },
				]
			: getWritingRunPipelineSteps(status);

	const resolvedTitle =
		title ||
		(phase === "fetch"
			? "Loading writing run"
			: STAGE_LABELS[status] || "Working on your draft");
	const resolvedDescription =
		description ||
		(phase === "fetch"
			? "Fetching run details and current progress."
			: STAGE_DESCRIPTIONS[status] ||
				"Fetching competitor pages, your own page, and identifying content gaps.");

	return (
		<div className="monitoring__analysis-loading">
			<div className="monitoring__analysis-loading-glow" aria-hidden />
			<div className="monitoring__analysis-loading-inner">
				<div className="monitoring__analysis-loading-icon">
					{phase === "fetch" ? (
						<Loader2 size={32} className="monitoring__analysis-spinner" aria-hidden />
					) : (
						<Sparkles size={28} className="monitoring__analysis-spinner" aria-hidden />
					)}
				</div>
				<p className="monitoring__analysis-loading-kicker mb-1">
					{phase === "fetch" ? "Loading" : "AI Writing Assistant"}
				</p>
				<h4 className="monitoring__analysis-loading-title">{resolvedTitle}</h4>
				<p className="monitoring__analysis-loading-desc">{resolvedDescription}</p>
				{run ? (
					<div className="monitoring__writing-loading-meta">
						<ModeBadge mode={run.mode} />
						<span className="monitoring__writing-loading-meta-title">
							{run.recommendation_title || run.recommendation_ref || `Run ${run.id}`}
						</span>
					</div>
				) : null}
				<ol className="monitoring__analysis-loading-steps">
					{steps.map((step) => (
						<li
							key={step.label}
							className={[
								step.active ? "is-active" : "",
								step.done ? "is-done" : "",
							]
								.filter(Boolean)
								.join(" ") || undefined}
						>
							<span className="monitoring__analysis-loading-step-dot" aria-hidden />
							{step.label}
						</li>
					))}
				</ol>
			</div>
		</div>
	);
}

function HistoryLoadingSkeleton() {
	return (
		<div className="monitoring__writing-history-loading" aria-hidden>
			{[0, 1, 2].map((item) => (
				<div key={item} className="monitoring__writing-history-skeleton" />
			))}
		</div>
	);
}

function isTableRow(text) {
	const value = String(text || "").trim();
	return value.startsWith("|") && value.endsWith("|") && value.split("|").length > 2;
}

function parseTableCells(text) {
	return String(text || "")
		.trim()
		.slice(1, -1)
		.split("|")
		.map((cell) => cell.trim());
}

function reflowForMarkdown(text) {
	const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
	const output = [];
	let index = 0;

	while (index < lines.length) {
		const line = lines[index];
		if (!line.trim()) {
			index += 1;
			continue;
		}
		if (isTableRow(line)) {
			const rows = [line];
			let next = index + 1;
			while (next < lines.length && isTableRow(lines[next])) {
				rows.push(lines[next]);
				next += 1;
			}
			if (rows.length > 1) {
				const columns = Math.max(parseTableCells(rows[0]).length, 1);
				output.push(rows[0], `|${Array(columns).fill("---").join("|")}|`, ...rows.slice(1), "");
			} else {
				output.push(rows[0], "");
			}
			index = next;
			continue;
		}
		output.push(line, "");
		index += 1;
	}

	return output.join("\n");
}

function buildPlainMarkdown(segments) {
	const kept = (segments || []).filter((segment) => segment.type !== "removed");
	let markdown = "";
	kept.forEach((segment, index) => {
		if (index > 0) {
			markdown +=
				isTableRow(kept[index - 1].text) && isTableRow(segment.text)
					? "\n"
					: "\n\n";
		}
		markdown += segment.text || "";
	});
	return reflowForMarkdown(markdown);
}

function normalizeContent(content) {
	return {
		...EMPTY_CONTENT,
		...(content || {}),
		meta_keywords: Array.isArray(content?.meta_keywords)
			? content.meta_keywords
			: [],
	};
}

function buildSavePayloadFromState(content, editedBlocks, run) {
	const pageDiffSegments = (run?.result?.recommendation?.page_diff?.segments || []).map(
		normalizeDiffSegment
	);
	const hasDiff = run?.mode === "optimize" && pageDiffSegments.length > 0;
	const effectiveSegments = hasDiff
		? pageDiffSegments.map((segment) =>
				segment.type === "added" &&
				segment.edit_index != null &&
				editedBlocks[segment.edit_index] !== undefined
					? { ...segment, text: editedBlocks[segment.edit_index] }
					: segment
			)
		: [];
	const bodyMarkdown = hasDiff
		? buildPlainMarkdown(effectiveSegments)
		: content.body_markdown;
	const editedBlocksPayload = {};
	Object.entries(editedBlocks).forEach(([key, value]) => {
		editedBlocksPayload[String(key)] = value;
	});
	return {
		meta_title: content.meta_title,
		h1: content.h1,
		meta_description: content.meta_description,
		meta_keywords: [...content.meta_keywords],
		url_slug: content.url_slug,
		body_markdown: bodyMarkdown,
		edited_blocks: editedBlocksPayload,
	};
}

function savePayloadsEqual(left, right) {
	if (!left || !right) return left === right;
	const normalizePayload = (payload) => {
		const editedBlocks = {};
		Object.keys(payload.edited_blocks || {})
			.sort((a, b) => Number(a) - Number(b))
			.forEach((key) => {
				editedBlocks[key] = payload.edited_blocks[key];
			});
		return {
			meta_title: payload.meta_title ?? "",
			h1: payload.h1 ?? "",
			meta_description: payload.meta_description ?? "",
			meta_keywords: [...(payload.meta_keywords || [])],
			url_slug: payload.url_slug ?? "",
			body_markdown: String(payload.body_markdown ?? "").replace(/\r\n/g, "\n"),
			edited_blocks: editedBlocks,
		};
	};
	return (
		JSON.stringify(normalizePayload(left)) === JSON.stringify(normalizePayload(right))
	);
}

function formatDate(value) {
	if (!value) return "";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

function ModeBadge({ mode, long = false }) {
	const create = mode === "create";
	return (
		<span
			className={`monitoring__writing-mode ${create ? "monitoring__writing-mode--create" : "monitoring__writing-mode--optimize"
				}`}
		>
			{long
				? create
					? "Create new page"
					: "Optimize existing page"
				: create
					? "Create"
					: "Optimize"}
		</span>
	);
}

function StatusText({ status }) {
	const tone =
		status === "complete"
			? "ready"
			: status === "failed"
				? "failed"
				: "busy";
	const label =
		status === "complete"
			? "Draft ready"
			: status === "failed"
				? "Failed"
				: "In progress";
	return (
		<span className={`monitoring__writing-status monitoring__writing-status--${tone}`}>
			{label}
		</span>
	);
}

function HistoryRail({ websiteId, activeRunId, onSelect, onClose, runPersonaHints = {} }) {
	const [runs, setRuns] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [personaById, setPersonaById] = useState(() => new Map());

	useEffect(() => {
		if (websiteId == null || websiteId === "") {
			setPersonaById(new Map());
			return;
		}
		let cancelled = false;
		listMonitoringWritingAssistantPersonas(websiteId).then((res) => {
			if (cancelled || !res.ok) return;
			const next = new Map();
			(res.personas || []).forEach((persona) => {
				const id = Number(persona.id ?? persona.persona_id ?? persona.personaId);
				if (Number.isFinite(id)) next.set(id, persona);
			});
			setPersonaById(next);
		});
		return () => {
			cancelled = true;
		};
	}, [websiteId]);

	const loadHistory = useCallback(
		async ({ background = false } = {}) => {
			if (websiteId == null || websiteId === "") {
				setRuns([]);
				setLoading(false);
				setError("Missing website id.");
				return;
			}
			if (!background) setLoading(true);
			try {
				const result = await listMonitoringWritingAssistantRuns(websiteId);
				setRuns(unwrapRuns(result));
				setError(null);
			} catch (err) {
				setError(err?.message || "Could not load writing history.");
			} finally {
				if (!background) setLoading(false);
			}
		},
		[websiteId]
	);

	useEffect(() => {
		let cancelled = false;
		let timer;

		async function refresh(background) {
			if (cancelled) return;
			await loadHistory({ background });
			if (!cancelled) timer = window.setTimeout(() => refresh(true), HISTORY_REFRESH_MS);
		}

		refresh(false);
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [loadHistory]);

	return (
		<aside
			id="writing-assistant-history-panel"
			className="monitoring__writing-history-panel"
			aria-label="Writing history"
		>
			<div className="monitoring__writing-history-panel-head">
				<div className="monitoring__writing-history-panel-head-row">
					<h3 className="monitoring__writing-history-panel-title">History</h3>
					{onClose ? (
						<button
							type="button"
							className="monitoring__writing-history-panel-close"
							onClick={(event) => {
								event.currentTarget.blur();
								onClose();
							}}
							aria-label="Close history panel"
							title="Close"
						>
							<X size={16} aria-hidden />
						</button>
					) : null}
				</div>
			</div>
			<div className="monitoring__writing-history-panel-body">
				{loading ? (
					<HistoryLoadingSkeleton />
				) : error ? (
					<div className="alert alert-danger py-2 px-3 small" role="alert">
						{error}
					</div>
				) : runs.length === 0 ? (
					<div className="monitoring__writing-history-empty">
						No writing runs yet. Use AI Writing Assistant on a recommendation in
						Content Strategy, or pick a past run when one appears here.
					</div>
				) : (
					<div className="monitoring__writing-history-list">
					{runs.map((item) => {
						const id = Number(item.id);
						const active = id === activeRunId;
						const mergedItem =
							runPersonaHints[id] != null
								? { ...item, ...runPersonaHints[id] }
								: item;
						const showPersona = hasWritingRunPersona(mergedItem);
						const personaLabel = writingRunPersonaHistoryLabel(
							mergedItem,
							personaById
						);
						return (
							<button
								key={item.id}
								type="button"
								className={`monitoring__writing-history-item${active ? " is-active" : ""
									}`}
								onClick={() => onSelect(id)}
								aria-current={active ? "true" : undefined}
							>
								<span className="monitoring__writing-history-item-title">
									{item.recommendation_title || item.recommendation_ref || `Run ${item.id}`}
								</span>
								{showPersona ? (
									<p className="monitoring__analysis-history-persona mb-0">
										Persona:{" "}
										{personaLabel ??
											(writingRunPersonaId(mergedItem) != null
												? `#${writingRunPersonaId(mergedItem)}`
												: "—")}
									</p>
								) : null}
								<span className="monitoring__writing-history-item-meta">
									<ModeBadge mode={item.mode} />
									<StatusText status={item.status} />
								</span>
								<span className="monitoring__writing-history-item-date">
									{formatDate(item.created_at)}
								</span>
							</button>
						);
					})}
					</div>
				)}
			</div>
		</aside>
	);
}

function CreatePagePreview({ h1, bodyMarkdown }) {
	const trimmedH1 = String(h1 || "").trim();
	const body = stripDuplicateLeadingH1(bodyMarkdown, trimmedH1);
	const hasBody = Boolean(body.trim());

	if (!trimmedH1 && !hasBody) {
		return <p className="small text-muted mb-0">No page content was generated.</p>;
	}

	return (
		<div className="monitoring__writing-create-preview">
			{trimmedH1 ? (
				<div className="monitoring__writing-create-preview-h1 mb-3">
					<h1 className="writing-preview__md-h1 mb-0">{trimmedH1}</h1>
				</div>
			) : null}
			{hasBody ? (
				<div className="monitoring__writing-create-preview-body">
					<ReactMarkdown
						remarkPlugins={[remarkGfm]}
						components={CREATE_PREVIEW_MARKDOWN_COMPONENTS}
					>
						{body}
					</ReactMarkdown>
				</div>
			) : null}
		</div>
	);
}

function DiffLegend() {
	return (
		<div className="monitoring__writing-diff-legend" aria-label="Change legend">
			<span className="monitoring__writing-diff-legend-item">
				<span
					className="monitoring__writing-diff-legend-swatch monitoring__writing-diff-legend-swatch--added"
					aria-hidden
				/>
				Added
			</span>
			<span className="monitoring__writing-diff-legend-item">
				<span
					className="monitoring__writing-diff-legend-swatch monitoring__writing-diff-legend-swatch--removed"
					aria-hidden
				/>
				Removed
			</span>
			<span className="monitoring__writing-diff-legend-hint">
				Hover for rationale · use <Pencil size={12} aria-hidden /> to edit added blocks
			</span>
		</div>
	);
}

function currentMetaLabel(value, emptyLabel) {
	if (Array.isArray(value)) {
		const items = value.map((item) => String(item ?? "").trim()).filter(Boolean);
		return items.length ? items.join(", ") : emptyLabel;
	}
	const text = String(value ?? "").trim();
	return text || emptyLabel;
}

function resolveCurrentMetadata(content, ownPage) {
	return {
		title: currentMetaLabel(
			content?.original_meta_title ?? ownPage?.title,
			"No meta title found"
		),
		h1: currentMetaLabel(content?.original_h1 ?? ownPage?.h1, "No H1 found"),
		description: currentMetaLabel(
			content?.original_meta_description ?? ownPage?.meta_description,
			"No meta description found"
		),
		keywords: currentMetaLabel(
			content?.original_meta_keywords ?? ownPage?.meta_keywords,
			"No meta keywords found"
		),
	};
}

function Field({ label, hint, over = false, current, children }) {
	return (
		<div>
			<label className="form-label d-flex justify-content-between gap-3 small fw-semibold mb-1">
				<span>{label}</span>
				{hint ? (
					<span className={over ? "text-danger" : "text-muted"}>{hint}</span>
				) : null}
			</label>
			{children}
			{current != null ? (
				<p className="monitoring__writing-current-meta mb-0">Current: {current}</p>
			) : null}
		</div>
	);
}

function SafeMarkdown({ children }) {
	return (
		<ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
			{String(children || "")}
		</ReactMarkdown>
	);
}

function normalizeDiffSegment(segment) {
	if (!segment) return segment;
	const typeRaw = String(segment.type || "").toLowerCase();
	let type = segment.type;
	if (typeRaw === "remove" || typeRaw === "delete" || typeRaw === "deleted") {
		type = "removed";
	} else if (typeRaw === "insert" || typeRaw === "add") {
		type = "added";
	}
	return {
		...segment,
		type,
		edit_index: segment.edit_index ?? segment.editIndex ?? null,
		change: segment.change || segment.reasoning || segment.summary || "",
		details: segment.details || segment.detail || segment.explanation || "",
	};
}

function DiffTable({
	segments,
	onEdit,
	onHover,
	onLeave,
}) {
	const rows = segments.map((segment) => parseTableCells(segment.text));
	const header = rows[0] || [];
	const body = rows.slice(1);
	const headerSegment = segments[0];
	const headerAdded =
		headerSegment?.type === "added" && headerSegment?.edit_index != null;

	function cellContent(cell) {
		return <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>{cell}</ReactMarkdown>;
	}

	return (
		<div className="table-responsive mb-3">
			<table className="table table-sm table-bordered align-middle mb-0">
				<thead className="table-light">
					<tr
						className={
							headerSegment?.type === "added"
								? "table-success"
								: headerSegment?.type === "removed"
									? "table-danger"
									: ""
						}
						style={
							headerSegment?.type === "removed"
								? { textDecoration: "line-through" }
								: undefined
						}
						onMouseEnter={(event) => onHover(headerSegment, event)}
						onMouseLeave={onLeave}
					>
						{header.map((cell, index) => (
							<th key={index}>{cellContent(cell)}</th>
						))}
						{headerAdded ? (
							<th style={{ width: 42 }}>
								<button
									type="button"
									className="monitoring__writing-diff-edit-btn"
									onClick={() => onEdit(headerSegment.edit_index)}
									aria-label="Edit this AI-generated table heading"
									title="Edit AI-generated content"
								>
									<Pencil size={12} aria-hidden />
								</button>
							</th>
						) : null}
					</tr>
				</thead>
				<tbody>
					{body.map((cells, rowIndex) => {
						const segment = segments[rowIndex + 1];
						const added = segment.type === "added";
						const removed = segment.type === "removed";
						return (
							<tr
								key={`${segment.edit_index ?? rowIndex}-${rowIndex}`}
								className={added ? "table-success" : removed ? "table-danger" : ""}
								style={removed ? { textDecoration: "line-through" } : undefined}
								onMouseEnter={(event) => onHover(segment, event)}
								onMouseLeave={onLeave}
							>
								{cells.map((cell, cellIndex) => (
									<td key={cellIndex}>{cellContent(cell)}</td>
								))}
								{added && segment.edit_index != null ? (
									<td style={{ width: 42 }}>
										<button
											type="button"
											className="monitoring__writing-diff-edit-btn"
											onClick={() => onEdit(segment.edit_index)}
											aria-label="Edit this AI-generated table row"
											title="Edit AI-generated content"
										>
											<Pencil size={12} aria-hidden />
										</button>
									</td>
								) : null}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function BlockEditor({
	index,
	addedByIndex,
	value,
	onChange,
	onCancel,
	onApply,
}) {
	return (
		<div className="border rounded p-3 mb-3 bg-light">
			<p className="small fw-semibold mb-1">Edit AI-generated content</p>
			{addedByIndex.get(index)?.change ? (
				<p className="small text-muted mb-2">{addedByIndex.get(index).change}</p>
			) : null}
			<div className="writing-preview__callout-editor monitoring__writing-body-editor">
				<WritingPreviewMarkdownWysiwyg value={value} onChange={onChange} />
				<p className="writing-preview__callout-editor-hint text-muted small mb-0 mt-2">
					Use the toolbar to format; content is saved as markdown.
				</p>
			</div>
			<div className="d-flex justify-content-end gap-2 mt-2">
				<button type="button" className="btn btn-sm btn-light border" onClick={onCancel}>
					Cancel
				</button>
				<button type="button" className="btn btn-sm btn-primary" onClick={onApply}>
					Apply
				</button>
			</div>
		</div>
	);
}

function getSegmentHoverInfo(hovered, changeInfoByIndex) {
	if (!hovered || (hovered.type !== "added" && hovered.type !== "removed")) {
		return null;
	}

	if (hovered.change || hovered.details) {
		return {
			change: hovered.change,
			details: hovered.details,
			type: hovered.type,
		};
	}

	const indexed =
		hovered.edit_index != null ? changeInfoByIndex.get(hovered.edit_index) : null;
	if (indexed?.change || indexed?.details) {
		return {
			change: indexed.change,
			details: indexed.details,
			type: hovered.type,
		};
	}

	return { type: hovered.type };
}

function hoverInfoTitle(info) {
	if (info?.change) return info.change;
	if (info?.type === "removed") return "Content removed";
	return "AI-generated change";
}

function DiffPreview({
	segments,
	addedByIndex,
	changeInfoByIndex,
	editingIndex,
	editDraft,
	onEditStart,
	onEditDraft,
	onEditCancel,
	onEditApply,
}) {
	const [hoverState, setHoverState] = useState({ segment: null, anchorRect: null });
	const groups = useMemo(() => {
		const output = [];
		segments.forEach((segment) => {
			const previous = output[output.length - 1];
			if (isTableRow(segment.text) && previous?.type === "table") {
				previous.segments.push(segment);
			} else if (isTableRow(segment.text)) {
				output.push({ type: "table", segments: [segment] });
			} else {
				output.push({ type: "block", segment });
			}
		});
		return output;
	}, [segments]);

	const hoverInfo = getSegmentHoverInfo(hoverState.segment, changeInfoByIndex);

	const showHover = (segment, event) => {
		if (!segment || editingIndex != null) return;
		setHoverState({
			segment: normalizeDiffSegment(segment),
			anchorRect: event.currentTarget.getBoundingClientRect(),
		});
	};

	const hideHover = () => {
		setHoverState({ segment: null, anchorRect: null });
	};

	return (
		<div className="position-relative">
			<WritingPreviewFixedPopover
				className="monitoring__writing-diff-popover"
				visible={Boolean(hoverInfo && editingIndex == null)}
				anchorRect={hoverState.anchorRect}
				title={hoverInfo ? hoverInfoTitle(hoverInfo) : ""}
				sections={
					hoverInfo?.details
						? [{ label: "", text: hoverInfo.details }]
						: []
				}
			/>

			{groups.map((group, groupIndex) => {
				if (group.type === "table") {
					const editingTableBlock = group.segments.some(
						(segment) => segment.edit_index === editingIndex
					);
					return (
						<div key={`table-${groupIndex}`}>
							<DiffTable
								segments={group.segments}
								onEdit={onEditStart}
								onHover={showHover}
								onLeave={hideHover}
							/>
							{editingTableBlock ? (
								<BlockEditor
									index={editingIndex}
									addedByIndex={addedByIndex}
									value={editDraft}
									onChange={onEditDraft}
									onCancel={onEditCancel}
									onApply={onEditApply}
								/>
							) : null}
						</div>
					);
				}

				const segment = group.segment;
				const added = segment.type === "added";
				const removed = segment.type === "removed";
				const editable = added && segment.edit_index != null;
				const editing = editable && editingIndex === segment.edit_index;

				return (
					<div key={`${segment.type}-${segment.edit_index ?? groupIndex}-${groupIndex}`}>
						<div
							className={`monitoring__writing-diff-block rounded px-2 pt-2 mb-2 ${added ? "monitoring__writing-diff-block--added" : removed ? "monitoring__writing-diff-block--removed" : ""
								}${editable ? " monitoring__writing-diff-block--editable" : ""}`}
							style={removed ? { textDecoration: "line-through" } : undefined}
							onMouseEnter={(event) => showHover(segment, event)}
							onMouseLeave={hideHover}
						>
							<SafeMarkdown>{segment.text}</SafeMarkdown>
							{editable ? (
								<button
									type="button"
									className="monitoring__writing-diff-edit-btn monitoring__writing-diff-edit-btn--floating"
									onClick={() => onEditStart(segment.edit_index)}
									aria-label="Edit this AI-generated content"
									title="Edit AI-generated content"
								>
									<Pencil size={12} aria-hidden />
								</button>
							) : null}
						</div>

						{editing ? (
							<BlockEditor
								index={editingIndex}
								addedByIndex={addedByIndex}
								value={editDraft}
								onChange={onEditDraft}
								onCancel={onEditCancel}
								onApply={onEditApply}
							/>
						) : null}
					</div>
				);
			})}
		</div>
	);
}

function RunHeader({ run }) {
	const title = run.recommendation_title || run.recommendation_ref || `Run ${run.id}`;
	const targetUrl = run.result?.target_url;
	return (
		<div className="monitoring__writing-run-header">
			<ModeBadge mode={run.mode} long />
			<h2 className="monitoring__writing-run-title">{title}</h2>
			<div className="monitoring__writing-run-meta">
				{targetUrl ? (
					<a href={targetUrl} target="_blank" rel="noopener noreferrer" className="text-break">
						{targetUrl}
					</a>
				) : null}
				<span>Created {formatDate(run.created_at)}</span>
			</div>
		</div>
	);
}

function WritingRunToolbar({ run, returnTo, onBack, actions }) {
	return (
		<header className="monitoring__writing-toolbar">
			{returnTo || actions ? (
				<div className="monitoring__writing-toolbar-row">
					<div className="monitoring__writing-toolbar-nav">
						{returnTo ? (
							<button
								type="button"
								className="monitoring__writing-toolbar-back"
								onClick={onBack}
							>
								<ChevronLeft size={14} aria-hidden />
								Back to Content Strategy
							</button>
						) : null}
					</div>
					{actions ? (
						<div className="monitoring__writing-toolbar-actions">{actions}</div>
					) : null}
				</div>
			) : null}
			<RunHeader run={run} />
		</header>
	);
}

function RunWorkbench({
	websiteId,
	runId,
	returnTo,
	isHistoryOpen,
	isContextOpen,
	onToggleHistory,
	onToggleContext,
	onRunPersonaLoaded,
}) {
	const navigate = useNavigate();
	const [run, setRun] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [saved, setSaved] = useState(false);
	const [savedBaseline, setSavedBaseline] = useState(null);
	const [content, setContent] = useState(EMPTY_CONTENT);
	const [newKeyword, setNewKeyword] = useState("");
	const [bodyMode, setBodyMode] = useState("preview");
	const [editedBlocks, setEditedBlocks] = useState({});
	const [editingIndex, setEditingIndex] = useState(null);
	const [editDraft, setEditDraft] = useState("");
	const [isDownloading, setIsDownloading] = useState(false);
	const [isCopying, setIsCopying] = useState(false);
	const initializedSnapshot = useRef(null);

	const loadRun = useCallback(
		async ({ background = false } = {}) => {
			if (!background) setLoading(true);
			try {
				const result = await getMonitoringWritingAssistantRun(websiteId, runId);
				const value = unwrapRun(result, "Could not load this writing run.");
				setRun(value);
				setError(null);
				const hints = writingRunPersonaHistoryHints(value);
				if (hints) onRunPersonaLoaded?.(Number(value.id), hints);
				return value;
			} catch (err) {
				setError(err?.message || "Could not load this writing run.");
				return null;
			} finally {
				if (!background) setLoading(false);
			}
		},
		[websiteId, runId, onRunPersonaLoaded]
	);

	useEffect(() => {
		let cancelled = false;
		initializedSnapshot.current = null;
		setRun(null);
		setError(null);
		setSavedBaseline(null);

		async function load() {
			if (cancelled) return;
			await loadRun();
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [loadRun]);

	useEffect(() => {
		if (!shouldPollWritingRun(run)) return undefined;
		let cancelled = false;
		let timer;

		async function poll() {
			timer = window.setTimeout(async () => {
				if (cancelled) return;
				const updated = await loadRun({ background: true });
				if (!cancelled && shouldPollWritingRun(updated)) poll();
			}, RUN_POLL_MS);
		}
		poll();
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [loadRun, run]);

	useEffect(() => {
		if (!run?.content) return;
		const snapshot = `${run.id}:${run.status}`;
		if (initializedSnapshot.current === snapshot) return;
		initializedSnapshot.current = snapshot;
		const normalized = normalizeContent(run.content);
		const restored = {};
		Object.entries(run.content.edited_blocks || {}).forEach(([key, value]) => {
			restored[Number(key)] = value;
		});
		setContent(normalized);
		setBodyMode("preview");
		setSaved(false);
		setSaveError(null);
		setEditedBlocks(restored);
		setEditingIndex(null);
		setSavedBaseline(buildSavePayloadFromState(normalized, restored, run));
	}, [run]);

	const currentSavePayload = useMemo(
		() => (run ? buildSavePayloadFromState(content, editedBlocks, run) : null),
		[content, editedBlocks, run]
	);
	const isContentInitialized =
		run?.content != null &&
		initializedSnapshot.current === `${run.id}:${run.status}`;
	const hasUnsavedChanges = useMemo(() => {
		if (!isContentInitialized || !savedBaseline || !currentSavePayload) return false;
		return !savePayloadsEqual(savedBaseline, currentSavePayload);
	}, [isContentInitialized, savedBaseline, currentSavePayload]);

	const recommendation = run?.result?.recommendation;
	const analysisBackTo = writingRunAnalysisPath(run) || returnTo;
	const handleBackToAnalysis = useCallback(() => {
		if (analysisBackTo) navigate(analysisBackTo);
	}, [analysisBackTo, navigate]);
	const pageDiffSegments = useMemo(
		() => (recommendation?.page_diff?.segments || []).map(normalizeDiffSegment),
		[recommendation]
	);
	const hasDiff = run?.mode === "optimize" && pageDiffSegments.length > 0;
	const addedByIndex = useMemo(() => {
		const map = new Map();
		pageDiffSegments.forEach((segment) => {
			if (segment.type === "added" && segment.edit_index != null) {
				map.set(segment.edit_index, {
					text: segment.text || "",
					change: segment.change,
					details: segment.details,
				});
			}
		});
		return map;
	}, [pageDiffSegments]);
	const changeInfoByIndex = useMemo(() => {
		const map = new Map();
		pageDiffSegments.forEach((segment) => {
			if (segment.edit_index == null) return;
			map.set(segment.edit_index, {
				change: segment.change,
				details: segment.details,
				type: segment.type,
			});
		});
		return map;
	}, [pageDiffSegments]);
	const effectiveSegments = useMemo(
		() =>
			pageDiffSegments.map((segment) =>
				segment.type === "added" &&
					segment.edit_index != null &&
					editedBlocks[segment.edit_index] !== undefined
					? { ...segment, text: editedBlocks[segment.edit_index] }
					: segment
			),
		[pageDiffSegments, editedBlocks]
	);

	function markChanged() {
		setSaved(false);
		setSaveError(null);
	}

	function setField(key, value) {
		setContent((previous) => ({ ...previous, [key]: value }));
		markChanged();
	}

	function addKeyword() {
		const keyword = newKeyword.trim();
		if (keyword && !content.meta_keywords.includes(keyword)) {
			setField("meta_keywords", [...content.meta_keywords, keyword]);
		}
		setNewKeyword("");
	}

	function removeKeyword(keyword) {
		setField(
			"meta_keywords",
			content.meta_keywords.filter((item) => item !== keyword)
		);
	}

	async function handleSave() {
		if (saving || !hasUnsavedChanges) return;
		setSaving(true);
		setSaved(false);
		setSaveError(null);
		const payload = currentSavePayload;
		try {
			const result = await saveMonitoringWritingAssistantContent(websiteId, runId, payload);
			const updated = unwrapRun(result, "Could not save the draft.");
			setRun(updated);
			const normalized = normalizeContent(
				updated.content || {
					...content,
					...payload,
					edited_blocks: payload.edited_blocks,
				}
			);
			const restored = {};
			Object.entries(payload.edited_blocks || {}).forEach(([key, value]) => {
				restored[Number(key)] = value;
			});
			setContent(normalized);
			setEditedBlocks(restored);
			setSavedBaseline(buildSavePayloadFromState(normalized, restored, updated));
			setSaved(true);
		} catch (err) {
			setSaveError(err?.message || "Could not save the draft.");
		} finally {
			setSaving(false);
		}
	}

	function startBlockEdit(index) {
		setEditingIndex(index);
		setEditDraft(editedBlocks[index] ?? addedByIndex.get(index)?.text ?? "");
	}

	function applyBlockEdit() {
		if (editingIndex == null) return;
		setEditedBlocks((previous) => ({ ...previous, [editingIndex]: editDraft }));
		setEditingIndex(null);
		markChanged();
	}

	const exportPayload =
		currentSavePayload || buildSavePayloadFromState(content, editedBlocks, run);
	const canDownloadDocx =
		isRunWorkbenchReady(run) &&
		hasExportableWritingContent(exportPayload, hasDiff ? effectiveSegments : null);

	async function handleCopyDocument() {
		if (!run || isCopying || !canDownloadDocx) return;
		const payload = exportPayload;
		setIsCopying(true);
		try {
			await copyWritingAssistantDocument({
				run,
				payload,
				content,
				segments: hasDiff ? effectiveSegments : null,
			});
			toast.success("Document copied to clipboard.");
		} catch (err) {
			toast.error(err?.message || "Could not copy the document.");
		} finally {
			setIsCopying(false);
		}
	}

	async function handleDownloadDocx() {
		if (!run || isDownloading || !canDownloadDocx) return;
		const payload = exportPayload;
		setIsDownloading(true);
		try {
			const filename = await downloadWritingAssistantDocx({
				run,
				payload,
				content,
				segments: hasDiff ? effectiveSegments : null,
			});
			toast.success(`Downloaded ${filename}`);
		} catch (err) {
			toast.error(err?.message || "Could not download the draft.");
		} finally {
			setIsDownloading(false);
		}
	}

	function renderPanelToggles({ downloadDisabled = false } = {}) {
		return (
			<>
				<WritingAssistantCopyToggle
					onCopy={handleCopyDocument}
					disabled={downloadDisabled || !canDownloadDocx}
					isCopying={isCopying}
					placement="toolbar"
				/>
				<WritingAssistantDownloadToggle
					onDownload={handleDownloadDocx}
					disabled={downloadDisabled || !canDownloadDocx}
					isDownloading={isDownloading}
					placement="toolbar"
				/>
				<WritingAssistantHistoryToggle
					isOpen={isHistoryOpen}
					onToggle={onToggleHistory}
					placement="toolbar"
				/>
				<WritingAssistantContextToggle
					isOpen={isContextOpen}
					onToggle={onToggleContext}
					placement="toolbar"
				/>
			</>
		);
	}

	if (loading && !run) {
		return <WritingRunLoadingView phase="fetch" />;
	}

	if (error && !run) {
		return <div className="alert alert-danger">{error}</div>;
	}

	if (!run) {
		return <div className="alert alert-light border">This writing run is unavailable.</div>;
	}

	if (run.status === "failed") {
		return (
			<div>
				<WritingRunToolbar
					run={run}
					returnTo={analysisBackTo}
					onBack={handleBackToAnalysis}
					actions={
						<div className="monitoring__writing-toolbar-action-row">
							{renderPanelToggles({ downloadDisabled: true })}
						</div>
					}
				/>
				<div className="alert alert-danger d-flex align-items-start gap-2 mt-3" role="alert">
					<AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
					<span>{run.error_message || "The writing run failed."}</span>
				</div>
			</div>
		);
	}

	if (!isRunWorkbenchReady(run)) {
		const progressStatus = run.status === "gaps_ready" ? "drafting" : run.status;
		return (
			<div>
				<WritingRunToolbar
					run={run}
					returnTo={analysisBackTo}
					onBack={handleBackToAnalysis}
					actions={
						<div className="monitoring__writing-toolbar-action-row">
							{renderPanelToggles({ downloadDisabled: true })}
						</div>
					}
				/>
				<WritingRunLoadingView phase="progress" status={progressStatus} run={run} />
			</div>
		);
	}

	const facts = recommendation?.facts_to_verify || [];
	const currentMeta = resolveCurrentMetadata(content, recommendation?.own_page);

	const saveActions = (
		<>
			<div className="monitoring__writing-toolbar-action-row">
				{renderPanelToggles()}
				<button
					type="button"
					className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
					onClick={handleSave}
					disabled={saving || !hasUnsavedChanges}
				>
					{saving ? <Loader2 className="monitoring__writing-spin" size={16} aria-hidden /> : null}
					Save Draft
				</button>
			</div>
			{saved ? (
				<span className="small text-success d-inline-flex align-items-center gap-1">
					<Check size={14} aria-hidden /> Saved
				</span>
			) : null}
			{saveError ? <span className="small text-danger">{saveError}</span> : null}
		</>
	);

	return (
		<div className="monitoring__writing-workbench">
			<div className="monitoring__writing-workbench-columns">
				<div className="monitoring__writing-workbench-main">
					<WritingRunToolbar
						run={run}
						returnTo={analysisBackTo}
						onBack={handleBackToAnalysis}
						actions={saveActions}
					/>
					<div className="d-flex flex-column gap-4 mt-3">
				<section className="p-4" style={CARD_STYLE}>
					<h3 className="small fw-semibold text-uppercase text-muted mb-3">Page metadata</h3>
					<div className="d-flex flex-column gap-3">
						<Field
							label="Meta title (SEO)"
							hint={`${content.meta_title.length} / 60`}
							over={content.meta_title.length > 60}
							current={currentMeta.title}
						>
							<input
								className="form-control"
								value={content.meta_title}
								onChange={(event) => setField("meta_title", event.target.value)}
							/>
						</Field>
						<Field
							label="H1 heading"
							hint="what visitors see on the page"
							current={currentMeta.h1}
						>
							<input
								className="form-control"
								value={content.h1}
								onChange={(event) => setField("h1", event.target.value)}
							/>
						</Field>
						{run.mode === "create" ? (
							<Field label="Suggested URL slug">
								<input
									className="form-control"
									value={content.url_slug}
									onChange={(event) => setField("url_slug", event.target.value)}
								/>
							</Field>
						) : null}
						<Field
							label="Meta description"
							hint={`${content.meta_description.length} / 160`}
							over={content.meta_description.length > 160}
							current={currentMeta.description}
						>
							<textarea
								rows={2}
								className="form-control"
								value={content.meta_description}
								onChange={(event) => setField("meta_description", event.target.value)}
							/>
						</Field>
						<div>
							<label className="form-label small fw-semibold mb-1">Meta keywords</label>
							<div className="d-flex flex-wrap align-items-center gap-2 border rounded p-2">
								{content.meta_keywords.map((keyword) => (
									<span key={keyword} className="badge text-bg-light border d-inline-flex align-items-center gap-1">
										{keyword}
										<button
											type="button"
											className="btn btn-link text-muted p-0 d-inline-flex"
											onClick={() => removeKeyword(keyword)}
											aria-label={`Remove ${keyword}`}
										>
											<X size={12} aria-hidden />
										</button>
									</span>
								))}
								<input
									className="form-control form-control-sm border-0 flex-grow-1"
									style={{ minWidth: 100, width: "auto" }}
									placeholder="+ Add"
									value={newKeyword}
									onChange={(event) => setNewKeyword(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.preventDefault();
											addKeyword();
										}
									}}
									onBlur={addKeyword}
								/>
							</div>
							<p className="monitoring__writing-current-meta mb-0">
								Current: {currentMeta.keywords}
							</p>
						</div>
					</div>
				</section>

				<section className="p-4" style={CARD_STYLE}>
					<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
						<h3 className="small fw-semibold text-uppercase text-muted mb-0">Page content</h3>
						{hasDiff ? (
							<DiffLegend />
						) : (
							<div className="btn-group btn-group-sm" role="group" aria-label="Content display mode">
								<button
									type="button"
									className={`btn d-inline-flex align-items-center gap-1 ${bodyMode === "preview" ? "btn-primary" : "btn-outline-secondary"
										}`}
									onClick={() => setBodyMode("preview")}
								>
									<Eye size={13} aria-hidden /> Preview
								</button>
								<button
									type="button"
									className={`btn d-inline-flex align-items-center gap-1 ${bodyMode === "edit" ? "btn-primary" : "btn-outline-secondary"
										}`}
									onClick={() => setBodyMode("edit")}
								>
									<Pencil size={13} aria-hidden /> Edit
								</button>
							</div>
						)}
					</div>

					{hasDiff ? (
						<DiffPreview
							segments={effectiveSegments}
							addedByIndex={addedByIndex}
							changeInfoByIndex={changeInfoByIndex}
							editingIndex={editingIndex}
							editDraft={editDraft}
							onEditStart={startBlockEdit}
							onEditDraft={setEditDraft}
							onEditCancel={() => setEditingIndex(null)}
							onEditApply={applyBlockEdit}
						/>
					) : bodyMode === "preview" && run.mode === "create" ? (
						<CreatePagePreview h1={content.h1} bodyMarkdown={content.body_markdown} />
					) : bodyMode === "preview" ? (
						content.body_markdown ? (
							<SafeMarkdown>{content.body_markdown}</SafeMarkdown>
						) : (
							<p className="small text-muted mb-0">No page content was generated.</p>
						)
					) : (
						<div className="writing-preview__callout-editor monitoring__writing-body-editor">
							<WritingPreviewMarkdownWysiwyg
								value={content.body_markdown}
								onChange={(value) => setField("body_markdown", value)}
							/>
							<p className="writing-preview__callout-editor-hint text-muted small mb-0 mt-2">
								Use the toolbar to format; content is saved as markdown.
							</p>
						</div>
					)}
				</section>

				{facts.length > 0 ? (
					<section className="monitoring__writing-facts-section p-4" style={CARD_STYLE}>
						<FactsToVerifyList facts={facts} showLead />
					</section>
				) : null}
					</div>
				</div>
				<div
					className={`monitoring__writing-context-column${
						isContextOpen ? " is-open" : " is-closed"
					}`}
				>
					<WritingAssistantContextPanel run={run} onClose={onToggleContext} />
				</div>
			</div>
		</div>
	);
}

export default function MonitoringWritingAssistant({ websiteId }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const returnTo = safeReturnTo(searchParams.get("returnTo"));
	const [isHistoryOpen, setIsHistoryOpen] = useState(() => !returnTo);
	const [isContextOpen, setIsContextOpen] = useState(() => Boolean(returnTo));
	// const [startRunOpen, setStartRunOpen] = useState(false);
	const runIdValue = searchParams.get("runId");
	const parsedRunId =
		runIdValue != null && runIdValue !== "" ? Number(runIdValue) : null;
	const runId = Number.isFinite(parsedRunId) ? parsedRunId : null;
	const [runPersonaHints, setRunPersonaHints] = useState({});

	const rememberRunPersona = useCallback((id, hints) => {
		if (!Number.isFinite(id) || !hints) return;
		setRunPersonaHints((prev) => {
			const existing = prev[id];
			if (existing && JSON.stringify(existing) === JSON.stringify(hints)) {
				return prev;
			}
			return { ...prev, [id]: hints };
		});
	}, []);

	function selectRun(id) {
		const next = new URLSearchParams(searchParams);
		next.set("runId", String(id));
		next.delete("returnTo");
		setSearchParams(next, { replace: true });
	}

	// function openStartRun() {
	// 	setStartRunOpen(true);
	// }

	function toggleHistory() {
		setIsHistoryOpen((open) => {
			const next = !open;
			if (next) setIsContextOpen(false);
			return next;
		});
	}

	function toggleContext() {
		setIsContextOpen((open) => {
			const next = !open;
			if (next) setIsHistoryOpen(false);
			return next;
		});
	}

	return (
		<div className="monitoring__writing-assistant">
			<div className="monitoring__writing-layout">
				<div
					className={`monitoring__writing-history-column${
						isHistoryOpen ? " is-open" : " is-closed"
					}`}
				>
					<HistoryRail
						websiteId={websiteId}
						activeRunId={runId}
						onSelect={selectRun}
						onClose={toggleHistory}
						runPersonaHints={runPersonaHints}
					/>
				</div>
				<main className="monitoring__writing-content monitoring__writing-content--workbench">
					{runId == null ? (
						<>
							<div className="monitoring__writing-page-toolbar">
								<div className="monitoring__writing-toolbar-action-row">
									<WritingAssistantCopyToggle disabled placement="toolbar" />
									<WritingAssistantDownloadToggle disabled placement="toolbar" />
									<WritingAssistantHistoryToggle
										isOpen={isHistoryOpen}
										onToggle={toggleHistory}
										placement="toolbar"
									/>
									<WritingAssistantContextToggle
										isOpen={isContextOpen}
										onToggle={toggleContext}
										disabled
										placement="toolbar"
									/>
								</div>
							</div>
							<div
								className="monitoring__writing-empty p-5 text-center"
								style={CARD_STYLE}
							>
							<div className="monitoring__writing-empty-icon" aria-hidden>
								<Sparkles size={28} />
							</div>
							<h3 className="monitoring__writing-empty-title">No run selected</h3>
							<p className="monitoring__writing-empty-text text-muted mb-0">
								Use AI Writing Assistant on an own-site recommendation in Content
								Strategy, or pick a past run from history.
							</p>
							</div>
						</>
					) : (
						<RunWorkbench
							key={runId}
							websiteId={websiteId}
							runId={runId}
							returnTo={returnTo}
							isHistoryOpen={isHistoryOpen}
							isContextOpen={isContextOpen}
							onToggleHistory={toggleHistory}
							onToggleContext={toggleContext}
							onRunPersonaLoaded={rememberRunPersona}
						/>
					)}
				</main>
			</div>

			{/* <WritingAssistantStartRunModal
				isOpen={startRunOpen}
				onClose={() => setStartRunOpen(false)}
				websiteId={websiteId}
			/> */}
		</div>
	);
}
